import { useState, useEffect, useRef } from "react";

import { Frame, List } from "@react95/core";

import { useRightClick } from "../../context/RightClickContext";
import { useApp } from "../../context/AppContext";
import { useDesktop } from "../../context/DesktopContext";

import RightClickMenu from "../UI/RightClickMenu";
import {IExploreHTMLFile} from "../UI/IconImage";

import { QuestWindowDef } from "../Modal/QuestModal";
import { AboutWindowDef } from "../Modal/AboutMe";
import { ChatClientDef } from "../Modal/ChatClient";
import { MailClientDef } from "../Modal/MailClient";
import { SystemPropertiesDef } from "../Modal/SystemProperties";
import { InternetClientDef } from "../Modal/InternetClient";

import { Confcp102, Winpopup3, FolderExe, Notepad, MicrosoftExchange } from "@react95/icons";

export default function Desktop(props: any) {
  const { openModal } = useApp();

  const { icons, selectedIcons, setSelectedIcons, addIcon, updateIconPosition } = useDesktop();

  const { closeMenu } = useRightClick();

  const iconsInitializedRef = useRef(false);

  // refs to track recent interactions so a following click doesn't clear selection
  const selectionJustEndedRef = useRef(false);
  const dragJustEndedRef = useRef(false);

  // drag state
  const [dragging, setDragging] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hoveredIcon, setHoveredIcon] = useState<string | null>(null);

  // selection box state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState({ x: 0, y: 0 });
  const [selectionEnd, setSelectionEnd] = useState({ x: 0, y: 0 });

  // initialize default icons
  useEffect(() => {
    if (!iconsInitializedRef.current) {
      addIcon(
        {
          id: "chat-client",
          label: "eMeSeN Messenger",
          iconComponent: ChatClientDef.icon,
          onClick: () => openModal(ChatClientDef),
        },
        { x: 7, y: 16 },
      );

      addIcon(
        {
          id: "quest-log",
          label: "Quest Log",
          iconComponent: QuestWindowDef.icon,
          onClick: () => openModal(QuestWindowDef),
        },
        { x: 44, y: 116 },
      );
      addIcon(
        {
          id: "mail-client",
          label: MailClientDef.title,
          iconComponent: <MicrosoftExchange variant="32x32_4" />,
          onClick: () => openModal(MailClientDef),
        },
        { x: 25, y: 215 },
      );
      addIcon(
        {
          id: "ctf-site",
          label:  "CTF Thing",
          iconComponent: <IExploreHTMLFile size={32}/>,
          onClick: () => {
            console.log("[Desktop] opening CTF with initialURL: http://ctf.dom");
            openModal(InternetClientDef, { initialURL: "http://ctf.dom" }, "ctf-browser");
          },
        },
        { x: 115, y: 250 },
      );
      iconsInitializedRef.current = true;
    }
  }, [addIcon, openModal]);

  // drag handlers
  const handleIconMouseDown = (e: React.MouseEvent, iconId: string, x: number, y: number) => {
    if (e.button !== 0) return; // only left click
    e.stopPropagation();

    // if clicking on unselected icon, select only that one
    if (!selectedIcons.includes(iconId)) {
      setSelectedIcons([iconId]);
    }

    // start drag after a small delay to distinguish from click
    const startDrag = setTimeout(() => {
      setDragging(iconId);
      setDragOffset({
        x: e.clientX - x,
        y: e.clientY - y,
      });
    }, 150);

    const cleanup = () => {
      clearTimeout(startDrag);
      window.removeEventListener("mouseup", cleanup);
    };
    window.addEventListener("mouseup", cleanup);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging) return;

    const draggedIcon = icons.find((icon) => icon.id === dragging);
    if (!draggedIcon) return;

    const deltaX = e.clientX - dragOffset.x - draggedIcon.position.x;
    const deltaY = e.clientY - dragOffset.y - draggedIcon.position.y;

    // move all selected icons
    selectedIcons.forEach((iconId) => {
      const icon = icons.find((i) => i.id === iconId);
      if (icon) {
        updateIconPosition(iconId, icon.position.x + deltaX, icon.position.y + deltaY);
      }
    });
  };

  const handleMouseUp = () => {
    setDragging(null);
    // when a drag finishes, prevent the click event that follows from clearing selection
    dragJustEndedRef.current = true;
    setTimeout(() => {
      dragJustEndedRef.current = false;
    }, 50);
  };

  // global mouse listeners for dragging
  useEffect(() => {
    if (dragging) {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [dragging, dragOffset, handleMouseMove, handleMouseUp]);

  // handle desktop click to deselect
  const handleDesktopClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      // ignore the click if it immediately follows a selection or drag
      if (selectionJustEndedRef.current || dragJustEndedRef.current) {
        return;
      }

      setSelectedIcons([]);
      closeMenu();
    }
  };

  // handle desktop mouse down for selection box
  const handleDesktopMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // only left click
    if (e.target !== e.currentTarget) return; // only on desktop, not icons

    setIsSelecting(true);
    setSelectionStart({ x: e.clientX, y: e.clientY });
    setSelectionEnd({ x: e.clientX, y: e.clientY });
    closeMenu();
  };

  // handle selection box dragging
  const handleSelectionMove = (e: MouseEvent) => {
    if (!isSelecting) return;
    setSelectionEnd({ x: e.clientX, y: e.clientY });

    // calculate which icons are in selection box
    const minX = Math.min(selectionStart.x, e.clientX);
    const maxX = Math.max(selectionStart.x, e.clientX);
    const minY = Math.min(selectionStart.y, e.clientY);
    const maxY = Math.max(selectionStart.y, e.clientY);

    const selected = icons.filter((icon) => {
      const iconCenterX = icon.position.x + 40; // icon width / 2
      const iconCenterY = icon.position.y + 40; // approximate center
      return (
        iconCenterX >= minX &&
        iconCenterX <= maxX &&
        iconCenterY >= minY &&
        iconCenterY <= maxY
      );
    });

    setSelectedIcons(selected.map((icon) => icon.id));
  };

  const handleSelectionEnd = () => {
    setIsSelecting(false);
    // prevent the immediate click (which follows mouseup) from clearing selection
    selectionJustEndedRef.current = true;
    setTimeout(() => {
      selectionJustEndedRef.current = false;
    }, 50);
  };

  // arrange icons alphabetically at the top
  const handleArrangeIcons = () => {
    const gridSize = 100; // spacing between icons
    const startX = 20;
    const startY = 20;

    // sort icons alphabetically by label
    const sortedIcons = [...icons].sort((a, b) => a.label.localeCompare(b.label));

    sortedIcons.forEach((icon, index) => {
      const row = Math.floor(index / 1); // 1 column for now
      const col = index % 1;
      const newX = startX + col * gridSize;
      const newY = startY + row * gridSize;
      updateIconPosition(icon.id, newX, newY);
    });

    closeMenu();
  };

  // snap icons to nearest grid
  const handleLineUpIcons = () => {
    const gridSize = 100; // grid spacing
    const minX = 20;
    const minY = 20;

    icons.forEach((icon) => {
      // snap to nearest grid position
      const snappedX = Math.round((icon.position.x - minX) / gridSize) * gridSize + minX;
      const snappedY = Math.round((icon.position.y - minY) / gridSize) * gridSize + minY;
      updateIconPosition(icon.id, snappedX, snappedY);
    });

    closeMenu();
  };

  // global mouse listeners for selection box
  useEffect(() => {
    if (isSelecting) {
      window.addEventListener("mousemove", handleSelectionMove);
      window.addEventListener("mouseup", handleSelectionEnd);
      return () => {
        window.removeEventListener("mousemove", handleSelectionMove);
        window.removeEventListener("mouseup", handleSelectionEnd);
      };
    }
  }, [isSelecting, selectionStart, icons, handleSelectionMove, handleSelectionEnd]);

  return (
    <RightClickMenu
      items={
        <List width={"200px"}>
          <List.Item onClick={handleArrangeIcons}>Arrange Icons</List.Item>
          <List.Item onClick={handleLineUpIcons}>Line Up Icons</List.Item>
          <List.Divider />
          <List.Item>Paste</List.Item>
          <List.Item icon={<FolderExe variant="16x16_4" />}>
            <List width={"200px"}>
              <List.Item icon={<Notepad variant="16x16_4" />}>Text Document</List.Item>
            </List>
            New
          </List.Item>
          <List.Divider />
          <List.Item
            onClick={() => {
              openModal(SystemPropertiesDef);
              closeMenu();
            }}
          >
            Properties
          </List.Item>
        </List>
      }
      onOpen={() => setSelectedIcons([])}
    >
      <Frame
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "#5AA",
          margin: 0,
          padding: 0,
          zIndex: 0,
        }}
        onClick={handleDesktopClick}
        onMouseDown={handleDesktopMouseDown}
      >
        {/* render icons */}
        {icons.map((icon) => {
          const isSelected = selectedIcons.includes(icon.id);
          const isHovered = hoveredIcon === icon.id;

          const isMulti = selectedIcons.length > 1 && selectedIcons.includes(icon.id);

          const iconItems = (
            <List width={"200px"}>
              {!isMulti && (
                <>
                  <List.Item onClick={() => closeMenu()}>Open</List.Item>
                  <List.Divider />
                  <List.Item>Cut</List.Item>
                  <List.Item>Copy</List.Item>
                  <List.Divider />
                  <List.Item>Create Shortcut</List.Item>
                  <List.Item>Delete</List.Item>
                  <List.Item>Rename</List.Item>
                  <List.Divider />
                  <List.Item>Properties</List.Item>
                </>
              )}

              {isMulti && (
                <>
                  <List.Item>Cut</List.Item>
                  <List.Item>Copy</List.Item>
                  <List.Divider />
                  <List.Item>Delete</List.Item>
                  <List.Divider />
                  <List.Item>Properties</List.Item>
                </>
              )}
            </List>
          );

          return (
            <RightClickMenu
              key={icon.id}
              items={iconItems}
              onOpen={() => {
                if (!selectedIcons.includes(icon.id)) {
                  setSelectedIcons([icon.id]);
                }
              }}
              id={icon.id}
            >
              <div
                style={{
                  position: "absolute",
                  left: icon.position.x,
                  top: icon.position.y,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  width: "80px",
                  padding: "4px",
                  cursor: "default",
                  userSelect: "none",
                  backgroundColor: isSelected ? "rgba(0, 0, 128, 0.5)" : "transparent",
                  border: isSelected
                    ? "1px dotted white"
                    : isHovered
                      ? "1px solid rgba(255, 255, 255, 0.3)"
                      : "1px solid transparent",
                  borderRadius: 0,
                }}
                onMouseDown={(e) =>
                  handleIconMouseDown(e, icon.id, icon.position.x, icon.position.y)
                }
                onMouseEnter={() => setHoveredIcon(icon.id)}
                onMouseLeave={() => setHoveredIcon(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!selectedIcons.includes(icon.id)) {
                    setSelectedIcons([icon.id]);
                  }
                }}
                onDoubleClick={(e) => {
                  e.stopPropagation();
                  icon.onClick();
                }}
              >
                {icon.iconComponent}
                <p
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: "12px",
                    textAlign: "center",
                    color: "white",
                    textShadow: "1px 1px 2px black",
                    wordWrap: "break-word",
                    width: "100%",
                  }}
                >
                  {icon.label}
                </p>
              </div>
            </RightClickMenu>
          );
        })}

        {/* selection box */}
        {isSelecting && (
          <div
            style={{
              position: "fixed",
              left: Math.min(selectionStart.x, selectionEnd.x),
              top: Math.min(selectionStart.y, selectionEnd.y),
              width: Math.abs(selectionEnd.x - selectionStart.x),
              height: Math.abs(selectionEnd.y - selectionStart.y),
              border: "1px dotted white",
              backgroundColor: "rgba(0, 0, 128, 0.2)",
              pointerEvents: "none",
              zIndex: 1000,
            }}
          />
        )}
      </Frame>
    </RightClickMenu>
  );
}
